import { Type } from 'class-transformer';
import {
  ArrayMaxSize,
  IsArray,
  IsBoolean,
  IsIn,
  IsISO8601,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  Matches,
  Max,
  MaxLength,
  ValidateNested,
} from 'class-validator';
import { FIELD_VALUE_SOURCES, type FieldValueSource } from '../../label/field-value-source';
import { NullableTrimmedStringTransform } from '../../validation/nullable-trimmed-string.transformer';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';
import { ADDITIVE_FUNCTIONS, type AdditiveFunction } from './additive-function.enum';
import { BREAKDOWN_EXEMPTIONS, type BreakdownExemption } from './breakdown-exemption.enum';
import { IngredientAllergenDto } from './ingredient-allergen.dto';
import { E_NUMBER_PATTERN, IngredientComponentDto } from './ingredient-component.dto';
import {
  INGREDIENT_PHYSICAL_KINDS,
  type IngredientPhysicalKind,
} from './ingredient-physical-kind.enum';
import { IngredientVerticalOriginEntryDto } from './ingredient-vertical-origin-entry.dto';

/**
 * Written out in full rather than `PartialType(CreateIngredientDto)`:
 * `@nestjs/mapped-types` is a backend dependency and must never enter
 * `shared`, which the frontend also imports.
 */
export class UpdateIngredientDto {
  @IsOptional()
  @IsISO8601({ strict: true })
  expectedUpdatedAt?: string;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  name?: string;

  /** Denominazione legale in etichetta, se diversa dal nome di anagrafica; vuoto = nessuna. */
  @IsOptional()
  @IsString()
  @NullableTrimmedStringTransform()
  @MaxLength(200)
  labelName?: string | null;

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  synonyms?: string[];

  @IsOptional()
  @IsIn(INGREDIENT_PHYSICAL_KINDS)
  physicalKind?: IngredientPhysicalKind;

  @IsOptional()
  @IsIn(ADDITIVE_FUNCTIONS)
  additiveFunction?: AdditiveFunction | null;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  @Matches(E_NUMBER_PATTERN)
  additiveENumber?: string | null;

  @IsOptional()
  @IsIn(BREAKDOWN_EXEMPTIONS)
  breakdownExemption?: BreakdownExemption | null;

  /** Deliberate tri-state (see the `Ingredient.isCompound` schema comment).
   * `null` (not `undefined`) is how a caller explicitly reverts a declared
   * ingredient back to "undeclared" — `undefined` here leaves the column
   * untouched, per this DTO's own class docblock. */
  @IsOptional()
  @IsBoolean()
  isCompound?: boolean | null;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  densityGPerMl?: number | null;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  densitySource?: FieldValueSource | null;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  pieceWeightG?: number | null;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  pieceWeightSource?: FieldValueSource | null;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  @Max(1)
  ediblePortionFactor?: number | null;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  ediblePortionSource?: FieldValueSource | null;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  brand?: string | null;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientAllergenDto)
  allergens?: IngredientAllergenDto[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IngredientComponentDto)
  components?: IngredientComponentDto[];

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(11)
  @ValidateNested({ each: true })
  @Type(() => IngredientVerticalOriginEntryDto)
  verticalOrigin?: IngredientVerticalOriginEntryDto[] | null;
}
